/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: DeviceSipProfileFormView.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the Device SIP Profiles module.
----------------------------------------------------------------*/

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Globe, Navigation, Music, FileText, Headphones, Radio, Search, Copy, Lock, Plus } from 'lucide-react';
import { buildApiUrl, getApiHeaders } from '../../config/api';
import { useNotifications } from '../../contexts/NotificationContext';
import { useAuthStore } from '../../store/auth';
import { Toggle } from '../../components/ui/Toggle';
import { SimpleTooltip } from '../../components/ui/SimpleTooltip';
import { FixedActionBar } from '../../components/ui/FixedActionBar';
import { useSaveWithAction } from '../../hooks/useSaveWithAction';
import { CodecField } from '../../components/ui/CodecField';
import { QuickSearchModal } from '../../components/ui/QuickSearchModal';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import { ModuleFormHeader } from '../../components/layouts';
import {
  navigateToDeviceSipProfileDuplicate,
} from './deviceSipProfilesActions';
import {
  fetchDeviceSipProfile,
  updateDeviceSipProfile,
  createDeviceSipProfile,
  fetchDeviceSipProfiles,
} from './api';
import { DeviceSipProfile, DeviceSipProfilePayload } from './types/deviceSipProfile.types';

interface SipProfile {
  id: number;
  name: string;
}

interface DeviceSipProfileFormData {
  name: string;
  description: string;
  enabled: boolean;
  baseSipProfileId: number | null;
  isDefault: boolean;

  // All settings as key-value pairs
  settings: Record<string, string | boolean | number>;
}

// Category configuration with icons - Based on comprehensive parameter reference
// Note: Names are translated via i18n using key: deviceSipProfiles:categories.{key}
const CATEGORIES = [
  { id: 1, key: 'network', icon: Globe },
  { id: 2, key: 'nat', icon: Navigation },
  { id: 3, key: 'media', icon: Headphones },
  { id: 4, key: 'codec', icon: Music },
  { id: 5, key: 'sipHeader', icon: FileText },
  { id: 6, key: 'transport', icon: Radio },
  { id: 7, key: 'security', icon: Lock },
];

// Field configuration by category - Comprehensive parameter reference
type FieldType = 'codec' | 'toggle' | 'select' | 'number' | 'text' | 'password';

interface FieldDefinition {
  name: string;
  fsName?: string;
  type: FieldType;
  default: string | number | boolean;
  options?: string[];
  possibleValues?: string;
  min?: number;
  max?: number;
  optional?: boolean;
  dependsOn?: string;
  placeholder?: string;
  hidden?: boolean;
}

const FIELD_CONFIG: Record<string, FieldDefinition[]> = {
  network: [
    { name: 'authAcl', fsName: 'auth-acl', type: 'select', default: 'lan', options: ['lan', 'wan', 'any'], possibleValues: 'lan | wan | any | custom ACL names' },
    { name: 'forceRegisterDomain', fsName: 'force-register-domain', type: 'toggle', default: true, possibleValues: 'true | false' },
  ],
  nat: [
    { name: 'aggressiveNatDetection', fsName: 'aggressive-nat-detection', type: 'toggle', default: true, possibleValues: 'true | false' },
    { name: 'rewriteContact', fsName: 'rewrite-contact', type: 'toggle', default: true, possibleValues: 'true | false' },
    { name: 'forceRport', fsName: 'force-rport', type: 'toggle', default: true, possibleValues: 'true | false' },
  ],
  media: [
    { name: 'rtpTimeoutSec', fsName: 'rtp-timeout-sec', type: 'number', default: 300, min: 60, max: 600, possibleValues: 'Range: 60–600' },
    { name: 'bypassMedia', fsName: 'bypass-media', type: 'toggle', default: false, possibleValues: 'true | false' },
    { name: 'disableTranscoding', fsName: 'disable-transcoding', type: 'toggle', default: false, possibleValues: 'true | false' },
    { name: 'rtpHoldTimeoutSec', fsName: 'rtp-hold-timeout-sec', type: 'number', default: 600, min: 120, max: 1800, possibleValues: 'Range: 120–1800', optional: true },
    { name: 'sessionTimeout', fsName: 'session-timeout', type: 'number', default: 1800, min: 300, max: 3600, possibleValues: 'Range: 300–3600', optional: true },
  ],
  codec: [
    { name: 'inboundCodecPrefs', fsName: 'inbound-codec-prefs', type: 'codec', default: 'PCMU,PCMA,G722,OPUS', possibleValues: 'PCMU | PCMA | G722 | OPUS | VP8 | H264 | G729' },
    { name: 'codecPrefs', fsName: 'codec-prefs', type: 'codec', default: 'PCMU,PCMA,G722,OPUS', possibleValues: 'PCMU | PCMA | G722 | OPUS | VP8 | H264 | G729' },
    { name: 'dtmfType', fsName: 'dtmf-type', type: 'select', default: 'rfc2833', options: ['rfc2833', 'info', 'inband'], possibleValues: 'rfc2833 | info | inband' },
    { name: 'rfc2833Pt', fsName: 'rfc2833-pt', type: 'number', default: 101, min: 96, max: 127, possibleValues: 'Range: 96–127', optional: true },
    { name: 'absoluteCodecString', fsName: 'absolute-codec-string', type: 'text', default: '', possibleValues: 'Codec list (same as above)', optional: true },
  ],
  sipHeader: [
    { name: 'sendRpid', fsName: 'send-rpid', type: 'toggle', default: true, possibleValues: 'true | false' },
    { name: 'sendPai', fsName: 'send-pai', type: 'toggle', default: true, possibleValues: 'true | false' },
    { name: 'sipDiversion', fsName: 'sip-diversion', type: 'toggle', default: true, possibleValues: 'true | false' },
    { name: 'sipCidType', fsName: 'sip_cid_type', type: 'select', default: 'pid', options: ['none', 'pid', 'rpid'], possibleValues: 'none | pid | rpid' },
  ],
  transport: [
    { name: 'wsBinding', fsName: 'ws-binding', type: 'toggle', default: false, possibleValues: 'true | false' },
    { name: 'wssBinding', fsName: 'wss-binding', type: 'toggle', default: false, possibleValues: 'true | false' },
    { name: 'tls', fsName: 'tls', type: 'toggle', default: false, possibleValues: 'true | false' },
    { name: 'tlsOnly', fsName: 'tls-only', type: 'toggle', default: false, dependsOn: 'tls', possibleValues: 'true | false' },
    { name: 'dtls', fsName: 'dtls', type: 'toggle', default: false, possibleValues: 'true | false' },
    { name: 'iceEnabled', fsName: 'ice-enabled', type: 'toggle', default: false, possibleValues: 'true | false' },
    { name: 'rtcpMux', fsName: 'rtcp-mux', type: 'toggle', default: false, possibleValues: 'true | false' },
    { name: 'registerExpires', fsName: 'register-expires', type: 'number', default: 3600, min: 60, max: 86400, possibleValues: 'Range: 60–86400', optional: true },
    { name: 'tlsVerifyPolicy', fsName: 'tls-verify-policy', type: 'select', default: 'none', options: ['none', 'peer', 'self-signed', 'optional'], dependsOn: 'tls', possibleValues: 'none | peer | self-signed | optional', optional: true },
  ],
  security: [
    { name: 'rtpSecureMedia', fsName: 'rtp-secure-media', type: 'select', default: 'optional', options: ['true', 'optional', 'false'], possibleValues: 'true | optional | false' },
    { name: 'requireSecureMedia', fsName: 'require-secure-media', type: 'select', default: 'false', options: ['true', 'false'], possibleValues: 'true | false' },
  ],
};

import { DeviceSipProfileSettingsTabs } from './components/DeviceSipProfileSettingsTabs';

export default function DeviceSipProfileFormView() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const duplicateId = searchParams.get('duplicate');
  const resetParam = searchParams.get('_reset');
  const isEditMode = id && id !== 'new';
  const navigate = useNavigate();
  const { t } = useTranslation(['deviceSipProfiles', 'common']);
  const { showSuccess, showError, showInfo } = useNotifications();
  const { user } = useAuthStore();

  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [sipProfiles, setSipProfiles] = useState<SipProfile[]>([]);
  const [activeTab, setActiveTab] = useState('network'); // Default tab
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [searchResults, setSearchResults] = useState<Record<string, number>>({}); // Count of results per tab

  // Quick List states
  const [showQuickList, setShowQuickList] = useState(false);
  const [quickListSearch, setQuickListSearch] = useState('');
  const [quickListProfiles, setQuickListProfiles] = useState<any[]>([]);
  const [isQuickListLoading, setIsQuickListLoading] = useState(false);
  const quickListLoadingRef = useRef(false);
  const lastIdRef = useRef<string | undefined>(id);
  const lastResetParamRef = useRef<string | null>(null);

  const [formData, setFormData] = useState<DeviceSipProfileFormData>({
    name: '',
    description: '',
    enabled: true,
    baseSipProfileId: null,
    isDefault: false,
    settings: {},
  });

  const [originalData, setOriginalData] = useState<DeviceSipProfileFormData>({
    name: '',
    description: '',
    enabled: true,
    baseSipProfileId: null,
    isDefault: false,
    settings: {},
  });

  const tenantDomainId = useMemo(() => {
    if (!user?.tenant?.id) {
      return undefined;
    }
    const parsed = Number(user.tenant.id);
    return Number.isFinite(parsed) ? parsed : undefined;
  }, [user?.tenant?.id]);

  // Check if there are unsaved changes
  const hasChanges = useMemo(() => {
    return JSON.stringify(formData) !== JSON.stringify(originalData);
  }, [formData, originalData]);

  // Reset form when navigating to 'new' after Save and New
  useEffect(() => {
    const currentId = id || 'new';
    const previousId = lastIdRef.current;
    const currentResetParam = resetParam;

    // If we're navigating from edit/create mode to 'new', reset the form
    // Also check for _reset query param which is set by useSaveWithAction
    const shouldReset = (!id || id === 'new') &&
      !isEditMode &&
      !duplicateId &&
      (previousId !== currentId ||
        (currentResetParam && currentResetParam !== lastResetParamRef.current));

    if (shouldReset) {
      const defaultSettings: Record<string, any> = {};
      Object.values(FIELD_CONFIG).flat().forEach((field) => {
        defaultSettings[field.name] = field.default;
      });
      setFormData({
        name: '',
        description: '',
        enabled: true,
        baseSipProfileId: null,
        isDefault: false,
        settings: defaultSettings,
      });
      setOriginalData({
        name: '',
        description: '',
        enabled: true,
        baseSipProfileId: null,
        isDefault: false,
        settings: defaultSettings,
      });

      // Clear the reset param from URL to prevent repeated resets
      if (currentResetParam) {
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.delete('_reset');
        const newSearch = newSearchParams.toString();
        window.history.replaceState({}, '', `${window.location.pathname}${newSearch ? `?${newSearch}` : ''}`);
      }
    }

    lastIdRef.current = currentId;
    lastResetParamRef.current = currentResetParam;
  }, [id, isEditMode, duplicateId, resetParam, searchParams]);

  // Initialize default settings
  useEffect(() => {
    const defaultSettings: Record<string, any> = {};
    Object.values(FIELD_CONFIG).flat().forEach((field) => {
      defaultSettings[field.name] = field.default;
    });
    setFormData((prev) => ({
      ...prev,
      settings: { ...defaultSettings, ...prev.settings },
    }));
  }, []);

  // Load SIP Profiles for dropdown and set default to "internal"
  useEffect(() => {
    const controller = new AbortController();

    const fetchSipProfiles = async () => {
      try {
        const response = await fetch(buildApiUrl('/api/sip-profiles?limit=100'), {
          headers: getApiHeaders(),
          signal: controller.signal,
        });

        if (!response.ok) {
          throw new Error('FAILED_TO_LOAD');
        }

        const result = await response.json();
        const profiles = Array.isArray(result.data) ? result.data : [];
        setSipProfiles(profiles);

        if (!isEditMode) {
          const internalProfile = profiles.find((p: SipProfile) => p.name?.toLowerCase() === 'internal');
          if (internalProfile && !formData.baseSipProfileId) {
            setFormData((prev) => ({
              ...prev,
              baseSipProfileId: internalProfile.id,
            }));
          }
        }
      } catch (error) {
        if (!controller.signal.aborted) {
          showError(t('deviceSipProfiles:loadError'));
        }
      }
    };

    fetchSipProfiles();

    return () => controller.abort();
  }, [formData.baseSipProfileId, isEditMode, showError, t]);

  // Load profile data in edit mode or duplicate mode
  useEffect(() => {
    const loadId = duplicateId || (isEditMode && id ? id : null);
    if (!loadId) return;

    const fetchProfile = async () => {
      setIsLoading(true);
      try {
        const result = await fetchDeviceSipProfile(loadId);
        const profile = result.data;

        // Map settings to form data
        const settingsMap: Record<string, any> = {};
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        profile.settings?.forEach((setting: any) => {
          const field = Object.values(FIELD_CONFIG)
            .flat()
            .find((f) => f.name === setting.name);

          if (field) {
            if (field.type === 'toggle') {
              settingsMap[setting.name] = setting.value === 'true';
            } else if (field.type === 'number') {
              settingsMap[setting.name] = setting.value ? parseInt(setting.value) : field.default;
            } else {
              settingsMap[setting.name] = setting.value || field.default;
            }
          }
        });

        const isDuplicate = Boolean(duplicateId);
        const copySuffix = t('common:copySuffix') || ' (Copy)';

        const loadedData = {
          name: isDuplicate ? `${profile.name || ''}${copySuffix}`.trim() : (profile.name || ''),
          description: profile.description || '',
          enabled: profile.enabled ?? true,
          baseSipProfileId: profile.baseSipProfileId || null,
          isDefault: false, // Always false for duplicates
          settings: settingsMap,
        };

        setFormData(loadedData);
        if (isDuplicate) {
          setOriginalData({
            name: '',
            description: '',
            enabled: true,
            baseSipProfileId: null,
            isDefault: false,
            settings: {},
          });
          showInfo(t('deviceSipProfiles:duplicateHint') || 'Edit the profile name and save to create a new entry.');
        } else {
          setOriginalData(loadedData);
        }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (error: any) {
        showError(error.message || t('deviceSipProfiles:loadError'));
      } finally {
        setIsLoading(false);
      }
    };

    fetchProfile();
  }, [id, isEditMode, duplicateId, t, showError, showInfo]);


  // Smart search: Calculate results per tab
  useEffect(() => {
    if (!searchQuery.trim()) {
      setSearchResults({});
      return;
    }

    const query = searchQuery.toLowerCase();
    const results: Record<string, number> = {};

    // Count matches per category
    CATEGORIES.forEach((category) => {
      const categoryFields = FIELD_CONFIG[category.key] || [];
      const matchCount = categoryFields.filter((field) => {
        if (field.hidden) return false;

        const fieldLabel = t(`deviceSipProfiles:${field.name}`).toLowerCase();
        const fieldFsName = (field.fsName || '').toLowerCase();
        const fieldName = field.name.toLowerCase();

        return (
          fieldLabel.includes(query) ||
          fieldFsName.includes(query) ||
          fieldName.includes(query)
        );
      }).length;

      results[category.key] = matchCount;
    });

    setSearchResults(results);

    // Auto-switch to first tab with results ONLY when search query changes
    const firstTabWithResults = CATEGORIES.find(cat => results[cat.key] > 0);
    if (firstTabWithResults) {
      setActiveTab(firstTabWithResults.key);
    }
  }, [searchQuery, t]); // Removed activeTab from dependencies

  // Quick List functions
  const loadQuickList = useCallback(async () => {
    if (quickListLoadingRef.current) {
      return;
    }

    quickListLoadingRef.current = true;
    setIsQuickListLoading(true);
    try {
      // Load all profiles initially (no search param for client-side filtering)
      const result = await fetchDeviceSipProfiles({
        page: 1,
        limit: 100,
        sortBy: 'name',
        sortOrder: 'asc',
      });
      setQuickListProfiles(result.data || []);
    } catch (error) {
      showError(t('deviceSipProfiles:quickListError'));
      setQuickListProfiles([]);
    } finally {
      setIsQuickListLoading(false);
      quickListLoadingRef.current = false;
    }
  }, [showError, t]);

  // Load quick list when modal opens
  useEffect(() => {
    if (showQuickList && quickListProfiles.length === 0) {
      loadQuickList();
    }
  }, [showQuickList, quickListProfiles.length, loadQuickList]);

  // Client-side filtering
  const filteredQuickList = useMemo(() => {
    const query = quickListSearch.trim().toLowerCase();
    if (!query) {
      return quickListProfiles;
    }

    return quickListProfiles.filter((profile) =>
      profile.name?.toLowerCase().includes(query) ||
      (profile.description || '').toLowerCase().includes(query)
    );
  }, [quickListProfiles, quickListSearch]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleQuickListSelect = useCallback((selectedProfile: any) => {
    setShowQuickList(false);
    setQuickListSearch('');
    navigate(`/settings/technology/device-profile/${selectedProfile.id}`);
  }, [navigate]);

  // Duplicate current profile from the fixed header action
  const handleDuplicateFromForm = () => {
    if (!isEditMode || !id) return;
    navigateToDeviceSipProfileDuplicate(navigate, id);
  };

  const handleSubmit = async (e?: React.FormEvent): Promise<DeviceSipProfile | undefined> => {
    // Prevent default form submission
    if (e) {
      e.preventDefault();
    }

    // Validation
    if (!formData.name.trim()) {
      showError(t('deviceSipProfiles:nameRequired'));
      return undefined;
    }

    if (!formData.baseSipProfileId) {
      showError(t('deviceSipProfiles:sofiaProfileRequired'));
      return undefined;
    }

    setIsSaving(true);
    try {
      // Map form data to settings array
      const settings = Object.entries(formData.settings)
        .filter(([_, value]) => value !== '' && value !== null && value !== undefined)
        .map(([name, value]) => ({
          name,
          value: String(value),
          settingType: 'param',
        }));

      const payload: DeviceSipProfilePayload = {
        name: formData.name,
        description: formData.description || null,
        baseSipProfileId: formData.baseSipProfileId,
        isDefault: formData.isDefault,
        enabled: formData.enabled,
        settings,
      };

      if (tenantDomainId !== undefined) {
        payload.domainId = tenantDomainId;
      }

      const responseData = isEditMode && id
        ? await updateDeviceSipProfile(id, payload)
        : await createDeviceSipProfile(payload);

      showSuccess(
        isEditMode
          ? t('deviceSipProfiles:updateSuccess')
          : t('deviceSipProfiles:createSuccess')
      );

      // Update originalData to reflect saved state
      setOriginalData({ ...formData });

      // Return saved data for useSaveWithAction
      return responseData.data;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      showError(error.message || t('deviceSipProfiles:saveError'));
      return undefined;
    } finally {
      setIsSaving(false);
    }
  };

  // Enhanced save handler for useSaveWithAction
  const handleSaveInternal = useCallback(async () => {
    const savedData = await handleSubmit();

    // If no changes were detected, return current data to allow navigation
    if (!savedData && isEditMode && id) {
      // Return current data with ID/UUID for navigation purposes
      return { id: Number(id), uuid: String(id) } as DeviceSipProfile;
    }

    return savedData;
  }, [handleSubmit, isEditMode, id]);

  // Use reusable hook for save actions
  const handleSaveWithAction = useSaveWithAction({
    onSave: handleSaveInternal,
    listPath: '/settings/technology/device-profile',
    newPath: '/settings/technology/device-profile/new',
    mode: isEditMode ? 'edit' : 'create',
    getEditPath: (savedData) => {
      // Device SIP Profiles use ID for navigation
      return `/settings/technology/device-profile/${savedData.id || savedData.uuid}`;
    },
    getFormData: () => ({
      formData
    }),
    storageKey: 'deviceSipProfiles:saveAndNewData'
  });

  // Handle cancel - restore original data (does NOT navigate)
  const handleCancel = () => {
    if (isEditMode) {
      // Restore original data
      setFormData({ ...originalData });
    } else {
      // Reset to defaults in create mode
      const defaultSettings: Record<string, any> = {};
      Object.values(FIELD_CONFIG).flat().forEach((field) => {
        defaultSettings[field.name] = field.default;
      });
      setFormData({
        name: '',
        description: '',
        enabled: true,
        baseSipProfileId: null,
        isDefault: false,
        settings: defaultSettings,
      });
    }
  };

  // Handle delete - show confirmation modal
  const handleDelete = () => {
    setShowDeleteModal(true);
  };

  // Confirm delete - actually delete the item and navigate to list
  const confirmDelete = async () => {
    if (!id || id === 'new') return;

    setIsDeleting(true);
    try {
      const { deleteDeviceSipProfile: deleteProfile } = await import('./api');
      await deleteProfile(id);

      showSuccess(t('deviceSipProfiles:deleteSuccess') || 'Device SIP profile deleted successfully');
      setShowDeleteModal(false);

      // Navigate to list after successful delete
      navigate('/settings/technology/device-profile');
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      showError(error.message || t('deviceSipProfiles:deleteError') || 'Failed to delete device SIP profile');
    } finally {
      setIsDeleting(false);
    }
  };

  const renderField = (field: FieldDefinition) => {
    const value = formData.settings[field.name] ?? field.default;
    const isDisabled = field.dependsOn ? !formData.settings[field.dependsOn] : false;
    const label = t(`deviceSipProfiles:${field.name}`);
    const tooltipText = t(`deviceSipProfiles:tooltips.${field.name}`);
    const tooltipContent = [tooltipText, field.possibleValues ? `${t('deviceSipProfiles:possibleValuesPrefix')}: ${field.possibleValues}` : '']
      .filter(Boolean)
      .join('\n\n') || label;

    const renderLabel = (labelClassName?: string) => (
      <SimpleTooltip content={tooltipContent} position="top">
        <div className="flex flex-col">
          <label className={labelClassName ?? 'text-sm font-medium text-foreground'}>
            {label}
          </label>
          {field.fsName && (
            <span className="text-xs text-muted-foreground mt-0.5">
              {field.fsName}
            </span>
          )}
        </div>
      </SimpleTooltip>
    );

    switch (field.type) {
      case 'codec':
        return (
          <React.Fragment key={field.name}>
            {renderLabel()}
            <CodecField
              value={String(value || '')}
              onChange={(newValue) =>
                setFormData((prev) => ({
                  ...prev,
                  settings: { ...prev.settings, [field.name]: newValue },
                }))
              }
              disabled={isDisabled}
              allowVideo={false} // Device profiles typically don't use video
              videoOnly={false}
              placeholder={t('deviceSipProfiles:selectCodecs')}
            />
          </React.Fragment>
        );

      case 'toggle':
        return (
          <React.Fragment key={field.name}>
            {renderLabel('text-sm font-medium text-foreground cursor-pointer')}
            <div className="flex justify-start">
              <Toggle
                value={!!value}
                onChange={(newValue) =>
                  setFormData((prev) => ({
                    ...prev,
                    settings: { ...prev.settings, [field.name]: newValue },
                  }))
                }
                disabled={isDisabled}
              />
            </div>
          </React.Fragment>
        );

      case 'select':
        return (
          <React.Fragment key={field.name}>
            {renderLabel()}
            <select
              value={String(value)}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  settings: { ...prev.settings, [field.name]: e.target.value },
                }))
              }
              disabled={isDisabled}
              className="px-3 py-2 text-sm border border-border rounded bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-primary focus:border-transparent disabled:opacity-50"
            >
              {(field.options || []).map((option) => (
                <option key={option} value={option}>
                  {t(`deviceSipProfiles:${field.name}Options.${option}`) || option.toUpperCase()}
                </option>
              ))}
            </select>
          </React.Fragment>
        );

      case 'number':
        return (
          <React.Fragment key={field.name}>
            {renderLabel()}
            <input
              type="number"
              value={value !== undefined && value !== null ? Number(value) : ''}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  settings: {
                    ...prev.settings,
                    [field.name]: e.target.value
                      ? parseInt(e.target.value, 10)
                      : field.optional
                        ? null
                        : field.default,
                  },
                }))
              }
              min={field.min}
              max={field.max}
              disabled={isDisabled}
              className="px-3 py-2 text-sm border border-border rounded bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-primary focus:border-transparent disabled:opacity-50"
              placeholder={field.placeholder}
            />
          </React.Fragment>
        );

      case 'text':
      case 'password':
      default:
        return (
          <React.Fragment key={field.name}>
            {renderLabel()}
            <input
              type={field.type === 'password' ? 'password' : 'text'}
              value={String(value || '')}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  settings: { ...prev.settings, [field.name]: e.target.value },
                }))
              }
              disabled={isDisabled}
              className="px-3 py-2 text-sm border border-border rounded bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary focus:border-transparent disabled:opacity-50"
              placeholder={field.placeholder}
            />
          </React.Fragment>
        );
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-muted-foreground">{t('common:loading')}</div>
      </div>
    );
  }

  return (
    <div className="p-6 pb-24">
      {/* Fixed Header */}
      <ModuleFormHeader
        icon={<Radio className="h-5 w-5 text-primary" />}
        title={isEditMode
          ? `${t('deviceSipProfiles:editProfile')}: ${formData.name || 'New Profile'}`
          : t('deviceSipProfiles:createProfile')
        }
        backButtonText={t('common:list')}
        onBack={() => navigate('/settings/technology/device-profile')}
        searchInput={
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input
              type="text"
              placeholder={t('common:search')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-10 py-1.5 text-sm border border-border rounded-lg bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
            />
            {searchQuery && (
              <SimpleTooltip
                content={t('common:clear')}
                position="top"
              >
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  ×
                </button>
              </SimpleTooltip>
            )}
          </div>
        }
        actions={[
          {
            icon: <Search className="h-5 w-5 text-muted-foreground" />,
            tooltip: t('deviceSipProfiles:quickList') || 'Quick List',
            onClick: () => {
              setShowQuickList(true);
              void loadQuickList();
            }
          },
          ...(isEditMode
            ? [{
              icon: <Copy className="h-5 w-5 text-muted-foreground" />,
              tooltip: t('common:duplicate') || 'Duplicate',
              onClick: handleDuplicateFromForm,
            }]
            : []),
          {
            icon: <Plus className="h-5 w-5 text-muted-foreground" />,
            tooltip: t('deviceSipProfiles:actions.add') || 'Add New',
            onClick: () => navigate('/settings/technology/device-profile/new'),
          },
        ]}
      />

      {/* Form */}
      <div className="mt-8">
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Profile Information */}
          <div className="bg-card p-3 sm:p-6 rounded-lg border border-border">
            {/* Responsive Grid Layout: 4 cols on desktop, 2 on tablet, 1 on mobile */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-center">
              {/* Row 1 */}
              <SimpleTooltip
                content={t('deviceSipProfiles:tooltips.profileName')}
                position="top"
              >
                <label className="text-sm font-medium text-foreground">
                  {t('deviceSipProfiles:profileName')}
                </label>
              </SimpleTooltip>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="px-3 py-2 text-sm border border-border rounded bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary focus:border-transparent"
                placeholder="default-device"
              />
              <SimpleTooltip
                content={t('deviceSipProfiles:tooltips.description')}
                position="top"
              >
                <label className="text-sm font-medium text-foreground">
                  {t('deviceSipProfiles:description')}
                </label>
              </SimpleTooltip>
              <input
                type="text"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="px-3 py-2 text-sm border border-border rounded bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary focus:border-transparent"
                placeholder="Optional description for this device profile"
              />

              {/* Row 2 */}
              <SimpleTooltip
                content={t('deviceSipProfiles:tooltips.enabled')}
                position="top"
              >
                <label className="text-sm font-medium text-foreground cursor-pointer">
                  {t('deviceSipProfiles:enabled')}
                </label>
              </SimpleTooltip>
              <div className="flex justify-start">
                <Toggle
                  value={formData.enabled}
                  onChange={(value) => setFormData({ ...formData, enabled: value })}
                />
              </div>
              <SimpleTooltip
                content={t('deviceSipProfiles:tooltips.sofiaProfileId')}
                position="top"
              >
                <label className="text-sm font-medium text-foreground">
                  {t('deviceSipProfiles:sofiaProfileId')} <span className="text-red-500">*</span>
                </label>
              </SimpleTooltip>
              <select
                value={formData.baseSipProfileId?.toString() || ''}
                onChange={(e) => setFormData({ ...formData, baseSipProfileId: e.target.value ? parseInt(e.target.value) : null })}
                className="px-3 py-2 text-sm border border-border rounded bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-primary focus:border-transparent"
                required
              >
                <option value="">{t('common:selectOption')}</option>
                {sipProfiles.map((profile) => (
                  <option key={profile.id} value={profile.id}>
                    {profile.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Settings Tabs */}
          <DeviceSipProfileSettingsTabs
            activeTab={activeTab}
            onTabChange={setActiveTab}
            categories={CATEGORIES}
            fieldConfig={FIELD_CONFIG}
            searchQuery={searchQuery}
            searchResults={searchResults}
            renderField={renderField}
            t={t}
          />
        </form>
      </div>

      {/* Fixed Action Bar */}
      <FixedActionBar
        onSaveWithAction={handleSaveWithAction}
        onCancel={handleCancel}
        onDelete={isEditMode ? handleDelete : undefined}
        isEditMode={isEditMode}
        isSaving={isSaving}
        isLoading={isLoading}
        saveText={t('common:save')}
        cancelText={t('common:cancel')}
        deleteText={t('common:delete') || 'Delete'}
        useSaveDropdown={true}
        saveDropdownStorageKey="deviceSipProfiles:saveActionPreference"
      />

      <QuickSearchModal
        isOpen={showQuickList}
        onClose={() => {
          setShowQuickList(false);
          setQuickListSearch('');
          setIsQuickListLoading(false);
          quickListLoadingRef.current = false;
        }}
        title={t('deviceSipProfiles:quickListTitle')}
        items={filteredQuickList}
        isLoadingInitial={isQuickListLoading}
        isSearching={false}
        searchValue={quickListSearch}
        onSearchChange={setQuickListSearch}
        onItemSelect={(profile) => handleQuickListSelect(profile)}
        currentItemId={id}
        renderItem={(profile, isCurrent) => (
          <button
            type="button"
            disabled={isCurrent}
            onClick={() => {
              if (isCurrent) return;
              handleQuickListSelect(profile);
            }}
            className={`w-full text-left p-3 rounded-md border border-border transition-colors ${isCurrent
                ? 'bg-primary/20 border-2 border-primary cursor-default opacity-60'
                : 'hover:bg-muted/40'
              }`}
          >
            <div className="font-medium text-foreground">{profile.name}</div>
            {profile.description && (
              <div className="text-sm text-muted-foreground mt-1">
                {profile.description}
              </div>
            )}
            <div className="text-xs text-muted-foreground mt-1">
              {profile.enabled ? t('common:enabled') : t('common:disabled')}
              {' • '}
              {t('deviceSipProfiles:settingsCount', { count: profile._count?.settings ?? 0 })}
            </div>
          </button>
        )}
        searchPlaceholder={t('deviceSipProfiles:quickListSearchPlaceholder')}
        emptyMessage={t('deviceSipProfiles:quickListEmpty.default')}
        noResultsMessage={t('deviceSipProfiles:quickListEmpty.search')}
        headerIcon={<Headphones className="h-5 w-5" />}
        minSearchChars={0}
      />

      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={confirmDelete}
        title={t('deviceSipProfiles:deleteConfirmTitle') || 'Delete Device SIP Profile'}
        message={t('deviceSipProfiles:deleteConfirmMessage') || 'Are you sure you want to delete this device SIP profile?'}
        confirmText={t('deviceSipProfiles:deleteConfirm') || t('common:delete')}
        cancelText={t('common:cancel')}
        type="danger"
        isLoading={isDeleting}
      />
    </div>
  );
}

